import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import VideoModal from "./VideoModal";

const ACCENT_LAYOUT = {
  large:   { col: "col-span-12 lg:col-span-8", aspect: "aspect-[16/9]" },
  wide:    { col: "col-span-12 lg:col-span-7", aspect: "aspect-[5/3]"  },
  tall:    { col: "col-span-6  lg:col-span-4", aspect: "aspect-[3/4]"  },
  default: { col: "col-span-6  lg:col-span-5", aspect: "aspect-[5/4]"  },
};

// Object-3D tiles get their own grid sizes (transparent, less rigid)
const OBJECT_LAYOUT = {
  large:   "col-span-6 lg:col-span-5",
  wide:    "col-span-6 lg:col-span-5",
  tall:    "col-span-4 lg:col-span-3",
  default: "col-span-4 lg:col-span-3",
};

function isVideoUrl(url) {
  if (!url) return false;
  const u = url.split("?")[0].toLowerCase();
  return u.endsWith(".mp4") || u.endsWith(".webm") || u.endsWith(".mov");
}

function CoverMedia({ project }) {
  const url = project.cover_url;
  if (!url) return <div className="absolute inset-0 bbf-accent-gradient opacity-30" />;
  if (isVideoUrl(url)) {
    return (
      <video src={url} autoPlay muted loop playsInline preload="metadata"
        className="w-full h-full object-cover grayscale group-hover:grayscale-0 opacity-85" />
    );
  }
  return (
    <img src={url} alt={project.title} loading="lazy"
      className="w-full h-full object-cover grayscale group-hover:grayscale-0 opacity-85" />
  );
}

/**
 * Per-tile parallax. Each tile has its own `speed` (-1..+1).
 * Movement is computed once per scroll tick (rAF) and applied via translate3d
 * on a wrapper element. No CSS transition on transform → smooth.
 */
function useParallax(speed) {
  const ref = useRef(null);
  const offsetRef = useRef(0);
  const rafRef = useRef(0);
  const targetRef = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !speed) return;

    let ticking = false;
    const compute = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      // -1..+1 progress: -1 = at the bottom of viewport, +1 = at the top
      const prog = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      targetRef.current = prog * 60 * speed; // px
      ticking = false;
    };

    const tick = () => {
      // lerp toward target so updates feel buttery
      offsetRef.current += (targetRef.current - offsetRef.current) * 0.18;
      if (el) el.style.transform = `translate3d(0, ${offsetRef.current.toFixed(2)}px, 0)`;
      rafRef.current = requestAnimationFrame(tick);
    };

    const onScroll = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(compute); }
    };

    compute();
    rafRef.current = requestAnimationFrame(tick);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", compute);
    };
  }, [speed]);

  return ref;
}

/**
 * Deterministic-ish speed picker so the order/feel doesn't change between
 * reloads, but each tile gets a varied amount of drift.
 */
function pickSpeed(seed, override) {
  if (typeof override === "number" && override !== 0) return Math.max(-1, Math.min(1, override));
  const arr = [0.6, -0.45, 0.32, -0.7, 0.5, -0.28, 0.78, -0.55, 0.22, -0.42];
  return arr[seed % arr.length];
}

function VideoTile({ project, idx, onClick }) {
  const layout = ACCENT_LAYOUT[project.accent] || ACCENT_LAYOUT.default;
  const speed = pickSpeed(idx, project.parallax_speed);
  const innerRef = useParallax(speed);

  return (
    <article
      data-testid={`portfolio-tile-${idx}`}
      data-kind="video"
      className={`bbf-portfolio-card relative ${layout.col} z-10 group cursor-pointer`}
      onClick={() => onClick(project)}
    >
      <div ref={innerRef} className="bbf-parallax-inner will-change-transform">
        <div className={`relative w-full ${layout.aspect} overflow-hidden bg-[#0a0a0a]`}>
          <CoverMedia project={project} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent pointer-events-none" />

          <div className="absolute top-3 left-3 right-3 flex items-center justify-between font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/70">
            <span>{String(idx + 1).padStart(2, "0")} · {project.client}</span>
            <span>{project.year}</span>
          </div>

          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <span className="bbf-btn-primary px-4 py-2 font-mono-jb text-[10px] uppercase tracking-[0.22em] font-bold">
              ▶ Смотреть
            </span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
            <p className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-[#c4b5fd]">
              / {project.category}
            </p>
            <h3 className="font-display uppercase text-base sm:text-xl lg:text-2xl leading-[1.05] tracking-tight mt-1.5">
              {project.title}
            </h3>
          </div>
        </div>
      </div>
    </article>
  );
}

function Object3DTile({ project, idx, onClick }) {
  const col = OBJECT_LAYOUT[project.accent] || OBJECT_LAYOUT.default;
  const speed = pickSpeed(idx + 7, project.parallax_speed);
  const innerRef = useParallax(speed);
  const tilt = ((idx * 41) % 9) - 4;
  const xOffset = ((idx * 53) % 16) - 8;
  const url = project.cover_url || "/objects/robot.png";
  const isVideo = isVideoUrl(url);

  return (
    <article
      data-testid={`portfolio-tile-${idx}`}
      data-kind="object3d"
      className={`relative ${col} z-0 group cursor-pointer flex items-center justify-center -my-6 sm:-my-10`}
      onClick={() => onClick(project)}
      style={{ marginLeft: `${xOffset}%` }}
      aria-label={project.title}
    >
      <div ref={innerRef} className="will-change-transform w-full">
        {isVideo ? (
          <video
            src={url}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="bbf-3d-float w-full max-w-[420px] mx-auto select-none"
            style={{ transform: `rotate(${tilt}deg)` }}
          />
        ) : (
          <img
            src={url}
            alt={project.title}
            loading="lazy"
            className="bbf-3d-float w-full max-w-[420px] mx-auto select-none"
            style={{ transform: `rotate(${tilt}deg)` }}
            draggable={false}
          />
        )}
      </div>
    </article>
  );
}

export default function Portfolio({ projects = [] }) {
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();

  const ordered = useMemo(() => {
    // Исправлено: убрали ограничение .slice(0, 10), теперь берем все видео-проекты
    const videos = projects.filter((p) => (p.kind || "video") !== "object3d");
    const objects = projects.filter((p) => p.kind === "object3d");
    
    if (objects.length === 0) return videos;
    
    const out = [];
    let oi = 0;
    
    videos.forEach((v, i) => {
      out.push(v);
      if (i % 2 === 1 && oi < objects.length) {
        out.push(objects[oi++]);
      }
    });
    
    while (oi < objects.length) out.push(objects[oi++]);
    return out;
  }, [projects]);

  const onClick = (project) => {
    if (project.article_slug) {
      navigate(`/article/${project.article_slug}`);
    } else {
      setOpen(project);
    }
  };

  return (
    <section
      data-testid="portfolio-section"
      id="work"
      className="relative bg-[#050505] border-t border-white/10 overflow-hidden"
    >
      <div className="px-5 sm:px-8 lg:px-12 pt-20 lg:pt-28">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="overline">Портфолио</p>
            <h2 className="font-display mt-3 uppercase text-2xl sm:text-3xl lg:text-4xl leading-[1] tracking-tight">
              Работы, в которые <span className="bbf-accent-text">мы верим.</span>
            </h2>
          </div>
          <p className="font-mono-jb text-[11px] uppercase tracking-[0.22em] text-white/40 max-w-xs">
            Имидж · LED · Live · CG · AI · Brand films.
          </p>
        </div>
      </div>

      <div className="relative">
        <div className="px-5 sm:px-8 lg:px-12 pb-24 lg:pb-32">
          <div className="grid grid-cols-12 gap-3 sm:gap-4 gap-y-12 lg:gap-y-20 items-start">
            {ordered.map((p, i) =>
              p.kind === "object3d" ? (
                <Object3DTile key={p.id || i} project={p} idx={i} onClick={onClick} />
              ) : (
                <VideoTile key={p.id || i} project={p} idx={i} onClick={onClick} />
              ),
            )}
          </div>
        </div>

        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-[#8b5cf6] opacity-[0.07] blur-[140px] pointer-events-none" />
        <div className="absolute bottom-1/4 -left-40 w-[400px] h-[400px] rounded-full bg-[#5b21b6] opacity-[0.08] blur-[140px] pointer-events-none" />
      </div>

      {open && (
        <VideoModal
          url={open.video_url || open.cover_url}
          title={open.title}
          subtitle={`${open.client} · ${open.category} · ${open.year}`}
          description={open.description}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}